import { useEffect, useState } from "react";
import { Pencil, Trash2, Plus, Copy, PackagePlus, Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { ImageField, money } from "./common";

const EMPTY = {
    name: "", description: "", category: "", image: "", price: "", sale_price: "",
    unit: "each", sold_by_weight: false, stock: "", variations: [], published: true, featured: false,
    allow_collection: true, allow_local_delivery: true, allow_courier_delivery: true,
};

export default function ProductsAdmin() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [q, setQ] = useState("");
    const [catFilter, setCatFilter] = useState("");
    const [open, setOpen] = useState(false);
    const [stockOpen, setStockOpen] = useState(null);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [stockForm, setStockForm] = useState({ variation: "", delta: 0, reason: "Manual adjustment" });

    const load = () =>
        Promise.all([
            api.get("/admin/products", { params: { q, category: catFilter } }),
            api.get("/admin/categories").catch(() => api.get("/categories")),
        ]).then(([p, c]) => {
            setProducts(p.data);
            setCategories(c.data);
        }).catch((err) => toast.error(formatApiError(err.response?.data?.detail)));

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, catFilter]);

    const openEditor = (p) => {
        setEditing(p || null);
        setForm(
            p
                ? {
                      name: p.name, description: p.description || "", category: p.category, image: p.image || "",
                      price: p.price ?? "", sale_price: p.sale_price ?? "", unit: p.unit || "each",
                      sold_by_weight: !!p.sold_by_weight,
                      stock: p.stock ?? "", variations: p.variations || [], published: p.published !== false,
                      featured: !!p.featured,
                      allow_collection: p.allow_collection !== false,
                      allow_local_delivery: p.allow_local_delivery !== false,
                      allow_courier_delivery: p.allow_courier_delivery !== false,
                  }
                : { ...EMPTY, category: categories[0]?.name || "" }
        );
        setOpen(true);
    };

    const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

    const save = async () => {
        if (!form.name.trim()) return toast.error("Name is required");
        if (!form.category) return toast.error("Choose a category");
        setSaving(true);
        const payload = {
            name: form.name, description: form.description, category: form.category, image: form.image,
            price: num(form.price), sale_price: num(form.sale_price), unit: form.sold_by_weight ? "per kg" : form.unit || "each",
            sold_by_weight: form.sold_by_weight,
            stock: form.sold_by_weight || !form.variations.length ? num(form.stock) : null,
            variations: form.sold_by_weight ? [] : form.variations.map((v) => ({ ...v, price: num(v.price), stock: v.stock === "" ? null : Number(v.stock) })),
            published: form.published, featured: form.featured,
            allow_collection: form.allow_collection, allow_local_delivery: form.allow_local_delivery,
            allow_courier_delivery: form.allow_courier_delivery,
        };
        try {
            if (editing) {
                await api.put(`/admin/products/${editing.id}`, payload);
                toast.success("Product updated");
            } else {
                await api.post("/admin/products", payload);
                toast.success("Product added");
            }
            setOpen(false);
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const act = async (fn, success) => {
        try {
            await fn();
            if (success) toast.success(success);
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const adjustStock = async () => {
        try {
            await api.post(`/admin/products/${stockOpen.id}/stock`, {
                variation: stockForm.variation || null,
                delta: Number(stockForm.delta),
                reason: stockForm.reason,
            });
            toast.success("Stock updated");
            setStockOpen(null);
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const setVariation = (i, patch) =>
        setForm({ ...form, variations: form.variations.map((v, idx) => (idx === i ? { ...v, ...patch } : v)) });

    return (
        <div data-testid="admin-products">
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <Input placeholder="Search products…" value={q} onChange={(e) => setQ(e.target.value)}
                    className="max-w-xs bg-white" data-testid="admin-products-search" />
                <Select value={catFilter || "all"} onValueChange={(v) => setCatFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-56 bg-white" data-testid="admin-products-catfilter">
                        <SelectValue placeholder="All categories" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All categories</SelectItem>
                        {categories.map((c) => (
                            <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <span className="text-sm text-slate-500" data-testid="admin-products-count">{products.length} products</span>
                <Button onClick={() => openEditor(null)} data-testid="admin-add-product"
                    className="ml-auto rounded-full bg-[#0033A0] hover:bg-[#002277]">
                    <Plus className="h-4 w-4 mr-2" /> Add Product
                </Button>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100" data-testid="admin-products-list">
                {products.map((p) => (
                    <div key={p.id} className="flex items-center gap-4 p-4 flex-wrap" data-testid={`admin-product-row-${p.id}`}>
                        <img src={p.image} alt="" className="h-14 w-14 rounded-lg object-cover bg-slate-100 shrink-0" />
                        <div className="flex-1 min-w-48">
                            <p className="font-heading font-bold text-slate-900 flex items-center gap-2 flex-wrap">
                                {p.name}
                                {p.published === false && <span className="rounded-full bg-slate-200 text-slate-600 text-xs px-2 py-0.5">Hidden</span>}
                                {p.featured && <span className="rounded-full bg-[#40E0D0]/30 text-[#0033A0] text-xs px-2 py-0.5">Featured</span>}
                                {p.sale_price != null && <span className="rounded-full bg-green-100 text-green-700 text-xs px-2 py-0.5">On offer</span>}
                            </p>
                            <p className="text-xs text-slate-500">
                                {p.category} · {p.price != null ? `${money(p.sale_price || p.price)} ${p.unit !== "each" ? p.unit : ""}` : "no price set"}
                                {p.variations?.length ? ` · ${p.variations.length} options` : ""}
                                {" · "}
                                {p.variations?.length
                                    ? `stock: ${p.variations.map((v) => `${v.name} ${v.stock ?? "—"}`).join(", ")}`
                                    : `stock: ${p.stock ?? "untracked"}`}
                            </p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <Button variant="outline" size="sm" className="rounded-full" data-testid={`admin-stock-${p.id}`}
                                onClick={() => { setStockOpen(p); setStockForm({ variation: p.variations?.[0]?.name || "", delta: 0, reason: "Manual adjustment" }); }}>
                                <PackagePlus className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" className="rounded-full" data-testid={`admin-toggle-publish-${p.id}`}
                                onClick={() => act(() => api.put(`/admin/products/${p.id}`, {
                                    name: p.name, description: p.description || "", category: p.category, image: p.image || "",
                                    price: p.price, sale_price: p.sale_price, unit: p.unit || "each", stock: p.stock,
                                    variations: p.variations || [], published: p.published === false, featured: !!p.featured,
                                    allow_collection: p.allow_collection !== false, allow_local_delivery: p.allow_local_delivery !== false,
                                    allow_courier_delivery: p.allow_courier_delivery !== false,
                                }), p.published === false ? "Product published" : "Product hidden")}>
                                {p.published === false ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                            </Button>
                            <Button variant="outline" size="sm" className="rounded-full" data-testid={`admin-duplicate-${p.id}`}
                                onClick={() => act(() => api.post(`/admin/products/${p.id}/duplicate`), "Duplicated (hidden until you publish it)")}>
                                <Copy className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" className="rounded-full" onClick={() => openEditor(p)} data-testid={`admin-edit-product-${p.id}`}>
                                <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" className="rounded-full text-red-600 border-red-200 hover:bg-red-50"
                                data-testid={`admin-delete-product-${p.id}`}
                                onClick={() => window.confirm(`Delete ${p.name}?`) && act(() => api.delete(`/admin/products/${p.id}`), "Deleted")}>
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Editor dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-product-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">{editing ? "Edit Product" : "Add Product"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="pf-name">Name *</Label>
                                <Input id="pf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pf-name" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Category *</Label>
                                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                    <SelectTrigger data-testid="pf-category"><SelectValue placeholder="Choose" /></SelectTrigger>
                                    <SelectContent>
                                        {categories.map((c) => (
                                            <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="pf-desc">Description</Label>
                            <Textarea id="pf-desc" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="pf-description" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Photo</Label>
                            <ImageField value={form.image} onChange={(url) => setForm({ ...form, image: url })} testId="pf-image" />
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="pf-price">{form.sold_by_weight ? "Price per kg £" : "Price £"}</Label>
                                <Input id="pf-price" type="number" min="0" step="0.01" value={form.price}
                                    onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="pf-price" placeholder="In-store only" />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="pf-sale">{form.sold_by_weight ? "Offer price per kg £" : "Offer price £"}</Label>
                                <Input id="pf-sale" type="number" min="0" step="0.01" value={form.sale_price}
                                    onChange={(e) => setForm({ ...form, sale_price: e.target.value })} data-testid="pf-sale-price" placeholder="None" />
                            </div>
                            {!form.sold_by_weight && (
                                <div className="space-y-1.5">
                                    <Label htmlFor="pf-unit">Unit</Label>
                                    <Input id="pf-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="pf-unit" placeholder="each / per kg" />
                                </div>
                            )}
                            {(form.sold_by_weight || !form.variations.length) && (
                                <div className="space-y-1.5">
                                    <Label htmlFor="pf-stock">{form.sold_by_weight ? "Stock (kg)" : "Stock"}</Label>
                                    <Input id="pf-stock" type="number" min="0" step={form.sold_by_weight ? "0.25" : "1"} value={form.stock}
                                        onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="pf-stock" placeholder="Untracked" />
                                </div>
                            )}
                        </div>
                        <div className="flex gap-6 flex-wrap">
                            <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                                <input type="checkbox" checked={form.sold_by_weight}
                                    onChange={(e) => setForm({ ...form, sold_by_weight: e.target.checked, ...(e.target.checked ? { variations: [], unit: "per kg" } : {}) })}
                                    className="accent-[#0033A0]" data-testid="pf-sold-by-weight" />
                                Sold by weight (priced per kg)
                            </label>
                            <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })}
                                    className="accent-[#0033A0]" data-testid="pf-published" />
                                Published (visible in shop)
                            </label>
                            <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                                    className="accent-[#0033A0]" data-testid="pf-featured" />
                                Featured on homepage
                            </label>
                        </div>

                        <div className="space-y-2">
                            <Label>Fulfilment options for this product</Label>
                            <div className="flex gap-6 flex-wrap">
                                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                                    <input type="checkbox" checked={form.allow_collection}
                                        onChange={(e) => setForm({ ...form, allow_collection: e.target.checked })}
                                        className="accent-[#0033A0]" data-testid="pf-allow-collection" />
                                    Click & Collect
                                </label>
                                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                                    <input type="checkbox" checked={form.allow_local_delivery}
                                        onChange={(e) => setForm({ ...form, allow_local_delivery: e.target.checked })}
                                        className="accent-[#0033A0]" data-testid="pf-allow-local" />
                                    Local delivery
                                </label>
                                <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                                    <input type="checkbox" checked={form.allow_courier_delivery}
                                        onChange={(e) => setForm({ ...form, allow_courier_delivery: e.target.checked })}
                                        className="accent-[#0033A0]" data-testid="pf-allow-courier" />
                                    Courier delivery
                                </label>
                            </div>
                            <p className="text-xs text-slate-400">Unticked options are refused at basket and checkout (checked again on the server).</p>
                        </div>

                        {/* Variations */}
                        {!form.sold_by_weight && (
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label>Variations / sizes / units</Label>
                                <Button type="button" variant="outline" size="sm" className="rounded-full" data-testid="pf-add-variation"
                                    onClick={() => setForm({ ...form, variations: [...form.variations, { name: "", price: "", unit: "each", stock: "" }] })}>
                                    <Plus className="h-4 w-4 mr-1" /> Add option
                                </Button>
                            </div>
                            {form.variations.length > 0 && (
                                <p className="text-xs text-slate-400">When a product has options, stock and price are managed per option.</p>
                            )}
                            {form.variations.map((v, i) => (
                                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`pf-variation-${i}`}>
                                    <Input className="col-span-4" placeholder="e.g. 1kg bag" value={v.name}
                                        onChange={(e) => setVariation(i, { name: e.target.value })} data-testid={`pf-var-name-${i}`} />
                                    <Input className="col-span-2" type="number" min="0" step="0.01" placeholder="Price" value={v.price ?? ""}
                                        onChange={(e) => setVariation(i, { price: e.target.value })} data-testid={`pf-var-price-${i}`} />
                                    <Input className="col-span-2" placeholder="Unit" value={v.unit}
                                        onChange={(e) => setVariation(i, { unit: e.target.value })} data-testid={`pf-var-unit-${i}`} />
                                    <Input className="col-span-2" type="number" min="0" placeholder="Stock" value={v.stock ?? ""}
                                        onChange={(e) => setVariation(i, { stock: e.target.value })} data-testid={`pf-var-stock-${i}`} />
                                    <Button type="button" variant="outline" size="sm" className="col-span-2 rounded-full text-red-600 border-red-200"
                                        onClick={() => setForm({ ...form, variations: form.variations.filter((_, idx) => idx !== i) })}
                                        data-testid={`pf-var-remove-${i}`}>
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                        )}
                        {form.sold_by_weight && (
                            <p className="text-xs text-slate-400" data-testid="pf-weight-note">
                                Sold by weight: customers pick a weight in 0.25 kg steps and pay the per-kg price. Stock is tracked in kg. Options/variations are not used.
                            </p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full" data-testid="pf-cancel">Cancel</Button>
                        <Button onClick={save} disabled={saving} className="rounded-full bg-[#0033A0] hover:bg-[#002277]" data-testid="pf-save">
                            {saving ? "Saving…" : "Save Product"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Stock adjust dialog */}
            <Dialog open={!!stockOpen} onOpenChange={() => setStockOpen(null)}>
                <DialogContent className="sm:max-w-md" data-testid="admin-stock-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Adjust stock — {stockOpen?.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        {(stockOpen?.variations || []).length > 0 && (
                            <div className="space-y-1.5">
                                <Label>Option</Label>
                                <Select value={stockForm.variation} onValueChange={(v) => setStockForm({ ...stockForm, variation: v })}>
                                    <SelectTrigger data-testid="stock-variation"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {stockOpen.variations.map((v) => (
                                            <SelectItem key={v.name} value={v.name}>{v.name} (current: {v.stock ?? "—"})</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="space-y-1.5">
                            <Label htmlFor="stock-delta">Adjustment{stockOpen?.sold_by_weight ? " in kg" : ""} (e.g. 10 to add, -3 to remove)</Label>
                            <Input id="stock-delta" type="number" step={stockOpen?.sold_by_weight ? "0.25" : "1"} value={stockForm.delta}
                                onChange={(e) => setStockForm({ ...stockForm, delta: e.target.value })} data-testid="stock-delta" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="stock-reason">Reason</Label>
                            <Input id="stock-reason" value={stockForm.reason}
                                onChange={(e) => setStockForm({ ...stockForm, reason: e.target.value })} data-testid="stock-reason" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setStockOpen(null)} className="rounded-full">Cancel</Button>
                        <Button onClick={adjustStock} className="rounded-full bg-[#0033A0] hover:bg-[#002277]" data-testid="stock-save">
                            Apply Adjustment
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
